import { ChangeEventHandler, InputHTMLAttributes, RefObject } from "react";
import cx from "classnames";
import Tiptap, { TipTapRef } from "./Tiptap/Tiptap";
import Markdown from "react-markdown";
import FormErrorMessage from "../ui/FormErrorMessage";

type TextAreaProps = InputHTMLAttributes<HTMLTextAreaElement> & {
  id: string;
  label?: string;
  hint?: string;
  value: string;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onTextFormatterChange?: (name: string, value: string) => void;
  className?: string;
  rows?: number;
  onClearError?: () => void;
  editorRef?: RefObject<TipTapRef>;
  errorMessage?: string;
};

const TextArea: React.FC<TextAreaProps> = ({
  id,
  label,
  hint,
  value,
  onChange,
  onTextFormatterChange,
  className,
  rows = 4,
  name = id,
  errorMessage,
  onClearError,
  disabled,
  editorRef,
  ...inputAttributes
}) => {
  const onValueChange = (value: string) => {
    if (onTextFormatterChange) {
      onTextFormatterChange(name, value);
    }
  };

  return (
    <div className="flex flex-col">
      {label && (
        <div className="mb-2 flex flex-col gap-1">
          <label htmlFor={id} className="body-16-semibold text-neutral-200">
            {label}
          </label>
          <span className="body-12-regular text-neutral-50">
            <Markdown>{hint}</Markdown>
          </span>
        </div>
      )}

      {onTextFormatterChange ? (
        <Tiptap
          ref={editorRef}
          value={value}
          onChange={onValueChange}
          disabled={disabled}
          errorMessage={errorMessage}
          {...inputAttributes}
        />
      ) : (
        <textarea
          id={id}
          value={value}
          onChange={onChange}
          rows={rows}
          name={name}
          className={cx(
            "border rounded-2xl border-neutral-25 outline-none py-[10px] px-4 mb-1 disabled:bg-neutral-15 focus:border-turquoise-100 body-14-regular text-neutral-200 placeholder:text-neutral-50",
            {
              "border-state-error-100": errorMessage,
              "resize-none": disabled,
            },
            className,
          )}
          disabled={disabled}
          onFocus={onClearError}
          {...inputAttributes}
        />
      )}
      {errorMessage && (
        <FormErrorMessage className="mt-2" message={errorMessage} />
      )}
    </div>
  );
};
export default TextArea;
